import { ColorEnum } from '@/constants/color'
import ForwardArrowIcon from '@/features/openTabs/icons/ForwardArrowIcon'
import IconButton from '@/features/ui/buttons/components/IconButton'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface SplitItemView {
    id: string
    name: string
    quantity: number
    totalPrice: string
    modifiers?: string[]
}

interface SplitItemRowProps {
    item: SplitItemView
    direction: 'forward' | 'back'
    disabled?: boolean
    onMove: () => void
}

const SplitItemRow: FC<SplitItemRowProps> = ({ item, direction, disabled, onMove }) => {
    const { t } = useTranslation('app')
    const { styles } = useStyles(stylesheet, { direction })

    return (
        <View style={styles.container}>
            <View style={styles.content}>
                <View style={styles.nameColumn}>
                    <Typography.Heading variant="h5">{item.name}</Typography.Heading>
                    {item.modifiers?.map((modifier) => (
                        <Typography.Description key={modifier} size="small" style={styles.modifier}>
                            {modifier}
                        </Typography.Description>
                    ))}
                </View>
                <View style={styles.priceColumn}>
                    <Typography.Heading variant="h5" style={styles.quantity}>
                        {t('openTabs.quantity.pieces', { count: item.quantity })}
                    </Typography.Heading>
                    <Typography.Description size="small" font="medium">
                        {item.totalPrice}
                    </Typography.Description>
                </View>
            </View>
            <IconButton
                accessibilityLabel={t(
                    direction === 'forward'
                        ? 'openTabs.split.moveToPayment'
                        : 'openTabs.split.moveFromPayment',
                    { item: item.name }
                )}
                accessibilityRole="button"
                Icon={ForwardArrowIcon}
                disabled={disabled}
                color="secondary"
                type="filled"
                size={44}
                onPress={onMove}
                style={styles.button}
            />
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
        paddingLeft: 12,
        paddingRight: 8,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
    },
    content: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        gap: 8,
        paddingTop: 2,
    },
    nameColumn: {
        flex: 1,
        minWidth: 0,
        gap: 4,
    },
    modifier: {
        color: ColorEnum.JET_500,
    },
    priceColumn: {
        alignItems: 'flex-end',
        gap: 4,
    },
    quantity: {
        color: ColorEnum.BRICK_600,
    },
    button: {
        borderRadius: 8,
        variants: {
            direction: {
                forward: {},
                back: {
                    transform: [{ rotate: '180deg' }],
                },
            },
        },
    },
})

export default SplitItemRow
